import type { SuggestionKeyDownProps } from '@tiptap/suggestion';
import { autoPlacement, autoUpdate, computePosition, hide, shift } from '@floating-ui/react';
import { type Command, Editor, posToDOMRect, type Range, ReactRenderer } from '@tiptap/react';

import { SlashMenu } from '@/components/slash-menu';

export const SlashCommand = (extraProps: SlashMenuExtraProps) => ({
  render: () => {
    let component: ReactRenderer<SlashMenuRef> | undefined;
    let handleClickOutside: ((event: MouseEvent) => void) | undefined;
    let cleanupPosition: (() => void) | undefined;

    const destroyComponent = () => {
      cleanupPosition?.();
      cleanupPosition = undefined;

      if (handleClickOutside) {
        document.removeEventListener('mousedown', handleClickOutside);
        handleClickOutside = undefined;
      }

      if (!component) {
        return;
      }

      component.destroy();
      component.element?.remove();
      component = undefined;
    };

    return {
      onStart: (props: SuggestionMenuProps) => {
        component = new ReactRenderer<SlashMenuRef, SlashMenuProps>(SlashMenu, {
          props: { ...props, ...extraProps, close: destroyComponent },
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        const element = component.element as HTMLElement;

        element.style.position = 'absolute';

        document.body.appendChild(element);

        cleanupPosition = updatePosition(props.editor, element, extraProps.getScroller(props.editor));

        handleClickOutside = (event: MouseEvent) => {
          // A modal layer over the menu (such as the AI credits refusal) is not a click outside it: closing the
          // menu there would drop the Generate Text prompt the user typed.
          if (element.hasAttribute('inert')) return;

          const path = event?.composedPath();

          const clickedInsideException = path.some((el: any) => Array.from(el.classList || []).includes('context-switch'));

          if (!element.contains(event.target as Node) && !clickedInsideException) {
            destroyComponent();
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
      },

      onUpdate(props: SuggestionMenuProps) {
        if (!component) {
          return;
        }

        component.updateProps({ ...props, ...extraProps });

        if (!props.clientRect) {
          return;
        }

        cleanupPosition?.();
        cleanupPosition = updatePosition(props.editor, component.element as HTMLElement, extraProps.getScroller(props.editor));
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          destroyComponent();
          return true;
        }

        return component?.ref?.onKeyDown?.(props) ?? false;
      },

      onExit() {
        destroyComponent();
      },
    };
  },
});

const updatePosition = (editor: Editor, element: HTMLElement, boundary?: HTMLElement) => {
  const virtualElement = {
    contextElement: boundary?.firstElementChild as HTMLElement | undefined,
    getBoundingClientRect: () => {
      try {
        return posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to);
      } catch {
        return new DOMRect(-9999, -9999, 0, 0);
      }
    },
  };

  return autoUpdate(virtualElement, element, () => {
    computePosition(virtualElement, element, {
      strategy: 'fixed',
      middleware: [
        shift(),
        hide({ strategy: 'referenceHidden', padding: 0 }),
        autoPlacement({
          alignment: 'start',
          autoAlignment: false,
          rootBoundary: boundary?.getBoundingClientRect(),
          allowedPlacements: ['bottom-start', 'top-start'],
        }),
        ...(boundary ? [hide({ strategy: 'referenceHidden', padding: 20 })] : []),
      ],
    }).then(({ x, y, strategy }) => {
      const viewportHeight = window.innerHeight;
      const refRect = virtualElement.getBoundingClientRect();

      // Check if the reference position is hidden behind the sticky header
      const isBehindStickyHeader = refRect.top < 120;

      const isAboveVisibleArea = refRect.bottom < 0;
      const isBelowVisibleArea = refRect.top > viewportHeight;

      const isOutOfView = isAboveVisibleArea || isBelowVisibleArea || isBehindStickyHeader;

      if (isOutOfView) {
        element.style.display = 'none';
      } else {
        element.style.position = strategy;
        element.style.left = `${x}px`;
        element.style.top = `${y}px`;
        element.style.width = 'max-content';
        element.style.display = 'block';
      }
    });
  });
};

export type SlashMenuRef = {
  onKeyDown?: ({ event }: { event: KeyboardEvent }) => boolean;
};

export interface SlashMenuProps {
  range: Range;
  query: string;
  editor: Editor;
  editorType: string;
  clientRect: () => DOMRect | null;
  command: (command?: Command) => void;
  // Closes the menu as Escape and a click outside it do.
  close: () => void;
}

type SuggestionMenuProps = Omit<SlashMenuProps, 'close'>;

export interface SlashMenuExtraProps {
  editorType: string;
  getScroller: (editor: Editor) => HTMLElement;
}
